/**
 * เป้า Cost per Result: one row per objective (labelled with its metric), the
 * target IN EFFECT today per platform. A new target starts on a chosen date
 * (default today); only the master may backdate, and then needs a reason.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  otherMetricTargetCount, resolveCprTarget, TARGET_OBJECTIVES, TARGET_PLATFORMS,
  type CprTargetRow, type TargetObjective, type TargetPlatform,
} from '@/services/targets/targetRules';
import { AWARENESS_METRICS, metricFor, type AwarenessMetric, type ResultMetric } from '@/services/targets/resultMetric';
import { proposeTarget, withdrawProposal } from '@/services/targets/targetsService';
import { TargetCell, type CellSaveInput } from './TargetCell';
import { keyPlatformLabel, COST_LABEL, OBJECTIVE_LABEL, RESULT_LABEL, type TargetRole } from './targetUi';

interface Props {
  workspaceId: string;
  targets: readonly CprTargetRow[];
  /** DEC3: the Awareness metric in force; only targets of the current metric are shown and edited. */
  awareness: AwarenessMetric;
  currency: string;
  today: string;
  role: TargetRole;
  me: string | null;
  reload: () => void;
}

const COLUMNS: readonly (TargetPlatform | null)[] = [null, ...TARGET_PLATFORMS];

const sameKey = (r: CprTargetRow, objective: TargetObjective, currency: string, platform: TargetPlatform | null) =>
  r.objective === objective && r.currency === currency && r.platform === platform;

/** DEC3 Q3: how many targets set against the other Awareness metric are kept (not shown, not lost). */
const KeptNote: React.FC<{ allTargets: readonly CprTargetRow[]; awareness: AwarenessMetric }> = ({ allTargets, awareness }) => {
  const { t } = useTranslation();
  const kept = otherMetricTargetCount(allTargets, 'awareness', awareness);
  const other = AWARENESS_METRICS.find((m) => m !== awareness) ?? awareness;
  return kept > 0
    ? <p className="mt-2 text-xs text-neutral-700">{t('targets.mapping.otherMetricKept', { metric: RESULT_LABEL[other](t), n: kept })}</p>
    : null;
};

export const CprTargetSection: React.FC<Props> = ({ workspaceId, targets: allTargets, awareness, currency, today, role, me, reload }) => {
  const { t } = useTranslation();
  const metricOf = (o: TargetObjective): ResultMetric => metricFor(o, awareness);
  // Q3: a target set against another definition is kept, but not shown or edited here (Task 3 notes how many).
  const targets = allTargets.filter((r) => r.metric === metricOf(r.objective));

  const cellFor = (objective: TargetObjective, platform: TargetPlatform | null) => {
    const inEffect = resolveCprTarget(targets, { objective, metric: metricOf(objective), currency, platform, date: today });
    const pendingRow = targets.find((r) => r.status === 'proposed' && sameKey(r, objective, currency, platform)) ?? null;
    const onSave = async ({ value, effectiveFrom, reason }: CellSaveInput): Promise<void> => {
      const from = effectiveFrom ?? today;
      // The RPC's version is the approved row for this exact start date.
      const approved = targets.find((r) => r.status === 'approved' && sameKey(r, objective, currency, platform) && r.effectiveFrom === from);
      await proposeTarget({
        workspaceId, objective, currency, platform, target: value, effectiveFrom: from,
        expectedVersion: approved?.id ?? null, reason: reason ?? undefined,
      });
    };
    return (
      <TargetCell
        key={platform ?? 'total'}
        testId={`target-${objective}-${platform ?? 'total'}`}
        keyLabel={`${OBJECTIVE_LABEL[objective](t)} · ${keyPlatformLabel(t, platform)}`}
        kind="target"
        value={inEffect?.target ?? null}
        currency={currency}
        pending={pendingRow ? { id: pendingRow.id, value: pendingRow.target, mine: pendingRow.proposedBy === me } : null}
        edit={role === 'viewer' ? 'none' : 'edit'}
        sendsForApproval={role === 'analyst'}
        canWithdraw={role !== 'viewer'}
        dates={{ today, allowPast: role === 'master' }}
        onSave={onSave}
        onWithdraw={(id) => withdrawProposal('target', id)}
        reload={reload}
      />
    );
  };

  /** The start dates of the targets in effect in this row; "—" when none. */
  const effectiveFromOf = (objective: TargetObjective): string => {
    const dates = COLUMNS
      .map((p) => resolveCprTarget(targets, { objective, metric: metricOf(objective), currency, platform: p, date: today })?.effectiveFrom ?? null)
      .filter((d): d is string => d !== null);
    const distinct = [...new Set(dates)].sort();
    return distinct.length > 0 ? distinct.join(', ') : t('targets.none');
  };

  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-6">
      <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('targets.cpr.heading')}</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <thead>
            <tr className="text-xs text-neutral-700">
              <th className="px-3 py-2">{t('targets.cpr.objective')}</th>
              {COLUMNS.map((p) => <th key={p ?? 'total'} className="px-3 py-2">{keyPlatformLabel(t, p)}</th>)}
              <th className="px-3 py-2">{t('targets.currency')}</th>
              <th className="px-3 py-2">{t('targets.cpr.effectiveFrom')}</th>
            </tr>
          </thead>
          <tbody>
            {TARGET_OBJECTIVES.map((objective) => (
              <tr key={objective} className="border-t border-neutral-100">
                <th scope="row" className="px-3 py-2 text-sm font-medium text-ink-900 whitespace-nowrap">
                  {OBJECTIVE_LABEL[objective](t)}
                  <span className="ml-1 text-xs font-normal text-neutral-700">({COST_LABEL[metricOf(objective)](t)})</span>
                </th>
                {COLUMNS.map((p) => cellFor(objective, p))}
                <td className="px-3 py-2 text-sm text-neutral-700">{currency}</td>
                <td className="px-3 py-2 text-sm text-neutral-700 whitespace-nowrap">{effectiveFromOf(objective)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <KeptNote allTargets={allTargets} awareness={awareness} />
    </section>
  );
};
